import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

// Déconnexion automatique après 5 minutes d'inactivité
const INACTIVITY_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  // initializing = tant qu'on n'a pas fini de restaurer la session (évite un logout au refresh)
  const [initializing, setInitializing] = useState(true);
  const userRef = useRef(null);
  userRef.current = user;

  const getStorageKey = (email, suffix) => (email ? `hb_${suffix}_${email}` : `hb_${suffix}`);

  const touchActivity = () => {
    try { sessionStorage.setItem("hb_last_activity", String(Date.now())); } catch { /* ignore */ }
  };

  const persist = (token, u) => {
    const tokenKey = getStorageKey(u.email, "token");
    const userKey = getStorageKey(u.email, "user");
    localStorage.setItem(tokenKey, token);
    localStorage.setItem(userKey, JSON.stringify(u));
    sessionStorage.setItem("hb_current_session", u.email);
    touchActivity();
    setUser(u);
  };

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    persist(res.data.access_token, res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    const current = userRef.current;
    if (current && current.email) {
      localStorage.removeItem(getStorageKey(current.email, "token"));
      localStorage.removeItem(getStorageKey(current.email, "user"));
    }
    const sessEmail = sessionStorage.getItem("hb_current_session");
    if (sessEmail) {
      localStorage.removeItem(getStorageKey(sessEmail, "token"));
      localStorage.removeItem(getStorageKey(sessEmail, "user"));
    }
    sessionStorage.removeItem("hb_current_session");
    sessionStorage.removeItem("hb_last_activity");
    // Compatibilité anciennes clés
    localStorage.removeItem("hb_token");
    localStorage.removeItem("hb_user");
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      const userData = res.data;
      const userKey = getStorageKey(userData.email, "user");
      localStorage.setItem(userKey, JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch {
      // Ne pas déconnecter sur une simple erreur réseau : un token réellement
      // invalide (401) est déjà géré par l'intercepteur api. On garde l'utilisateur en cache.
      return null;
    }
  }, []);

  // Restauration de session au chargement / refresh
  useEffect(() => {
    const currentSessionEmail = sessionStorage.getItem("hb_current_session");
    if (currentSessionEmail) {
      const token = localStorage.getItem(getStorageKey(currentSessionEmail, "token"));
      const userData = localStorage.getItem(getStorageKey(currentSessionEmail, "user"));

      // Vérifier l'inactivité (5 min) : si dépassée, on ne restaure pas
      const last = parseInt(sessionStorage.getItem("hb_last_activity") || "0", 10);
      const expired = last && (Date.now() - last > INACTIVITY_MS);

      if (token && userData && !expired) {
        try {
          setUser(JSON.parse(userData));
          touchActivity();
          setLoading(true);
          refresh().finally(() => { setLoading(false); setInitializing(false); });
          return;
        } catch {
          sessionStorage.removeItem("hb_current_session");
        }
      } else if (expired) {
        // Session expirée par inactivité
        localStorage.removeItem(getStorageKey(currentSessionEmail, "token"));
        localStorage.removeItem(getStorageKey(currentSessionEmail, "user"));
        sessionStorage.removeItem("hb_current_session");
        sessionStorage.removeItem("hb_last_activity");
      }
    }
    setInitializing(false);
  }, []);

  // Suivi de l'activité + déconnexion automatique après 5 min d'inactivité
  useEffect(() => {
    if (!user) return;
    const onActivity = () => touchActivity();
    const events = ["mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const interval = setInterval(() => {
      const last = parseInt(sessionStorage.getItem("hb_last_activity") || "0", 10);
      if (last && Date.now() - last > INACTIVITY_MS) {
        logout();
      }
    }, 15000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(interval);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh, loading, initializing }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
